import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import { Lock, ShieldCheck, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * One-shot screen shown right after a partner/merchant signs in for the
 * first time. AuthProvider/App.js gates routing on user.must_change_password
 * — until they set a new password, this is the ONLY page they can see.
 *
 * Reuses the login page's split-screen aesthetic so it feels like part of
 * the onboarding flow rather than a hard wall.
 */
export default function ForceChangePassword() {
  const { user, refreshMe, logout } = useAuth();
  const { app_name } = useBranding();
  const [show, setShow] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const head = (app_name || "BizAssist Online").trim().split(" ")[0];

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pw.current || !pw.next || !pw.confirm) { toast.error("Fill all three fields"); return; }
    if (pw.next.length < 10) { toast.error("New password must be at least 10 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    if (pw.next === pw.current) { toast.error("New password can't equal the temp password"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      // Pull the updated user object — must_change_password should now be false
      await refreshMe();
      toast.success("Password updated. Welcome aboard.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)] grid place-items-center px-4 py-10" data-testid="force-cpw-page">
      <div className="w-full max-w-md">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--accent)] font-medium mb-2">
          {head} · Welcome
        </div>
        <h1 className="font-display font-medium text-[28px] sm:text-[32px] leading-[1.05] tracking-tight text-[var(--ink)] mb-3">
          Set your new password
        </h1>
        <p className="text-[13.5px] text-[var(--muted)] leading-relaxed mb-7 max-w-sm">
          You're signed in as <b className="text-[var(--ink)]">{user?.email}</b>. Pick a strong password (at least 10 characters) before you start using your portal. You won't see this screen again.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="force-cpw-form">
          <Field label="Temp password (from your invite email)" icon={KeyRound}>
            <input
              data-testid="force-cpw-current"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className={inputCls}
              autoFocus
            />
          </Field>
          <Field
            label="New password"
            icon={Lock}
            trailing={
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                tabIndex={-1}
                className="text-[11px] text-[var(--muted)] hover:text-[var(--ink)] inline-flex items-center gap-1"
              >
                {show ? <><EyeOff size={11}/> Hide</> : <><Eye size={11}/> Show</>}
              </button>
            }
          >
            <input
              data-testid="force-cpw-next"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Confirm new password" icon={Lock}>
            <input
              data-testid="force-cpw-confirm"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className={inputCls}
            />
          </Field>

          <button
            type="submit"
            disabled={busy}
            data-testid="force-cpw-submit"
            className="w-full h-11 bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white font-medium rounded-sm text-[13.5px] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-all shadow-sm"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Update password & continue
          </button>

          <button
            type="button"
            onClick={logout}
            className="w-full text-center text-[12px] text-[var(--muted)] hover:text-[var(--ink)] transition-colors mt-2"
          >
            Sign out
          </button>

          <div className="pt-4 border-t border-[var(--line)] flex items-center justify-center gap-1.5 text-[11px] text-[var(--muted)]">
            <ShieldCheck size={11} className="text-[var(--accent)]" />
            Protected by industry-grade encryption
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children, trailing }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] uppercase tracking-[0.15em] text-[var(--muted)] font-semibold inline-flex items-center gap-1.5">
          {Icon && <Icon size={11} />} {label}
        </label>
        {trailing}
      </div>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3.5 h-11 border border-[var(--line-2)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/15 bg-[var(--surface)] outline-none rounded-sm text-[14px] transition-all";
